import { useMemo, useState } from "react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Tooltip,
  Marker,
} from "react-leaflet";
import L, { DivIcon } from "leaflet";
import "leaflet/dist/leaflet.css";

import {
  useDashboardGeoActivity,
  useDashboardGeoLayers,
  type GeoLayerName,
} from "@/api/dashboard";
import { BrandedLoader } from "@/components/shared/BrandedLoader";
import { UAE_CITY_ANCHORS } from "./uaeOutline";
import { TILES } from "@/components/map/basemaps";

// UAE bounding box — initial view centred to fit all 7 emirates.
const UAE_CENTER: [number, number] = [24.4, 54.0];
const UAE_BOUNDS: [[number, number], [number, number]] = [
  [22.4, 51.4], // SW
  [26.1, 56.5], // NE
];



/** Map a price-per-sqft value to a green→amber→red gradient.
 * Mirrors the visualMap stops the previous ECharts implementation used:
 * 500 → green, 5000 → red. Values outside the band saturate. */

/**
 * Geo Activity panel with an interactive light-grey OSM basemap.
 *
 * Replaces the previous static-Mapbox-image-behind-an-ECharts-scatter
 * approach. Using react-leaflet + CartoDB Positron tiles gives us:
 *   - true click-drag-zoom (operator F-007 ask)
 *   - free OSM tiles (operator preference for "Open Map data")
 *   - light-grey palette so the listing dots stand out
 *   - no Mapbox API token required
 */
/**
 * Inventory layers (MPPI-00188).
 *
 * The panel used to show one recency view — sale listings from the last 14
 * days, coloured by price-per-sqft. The operator asked for the whole market
 * instead, split by what a property IS, so the layers can be read against
 * each other.
 *
 * **Colour carries the segment, not the price.** The old ramp encoded
 * price-per-sqft in the dot colour; with five overlays that would be two
 * variables in one channel and neither would be readable. Price moved to the
 * tooltip, where a number belongs.
 *
 * Off-plan and developer sit adjacent in hue because they are adjacent in
 * meaning — most developer stock IS off-plan — while rental is deliberately
 * far from both: it is the one layer measured in rent, not price, and
 * confusing it with a sale price is the expensive mistake here.
 */
const LAYERS: {
  id: GeoLayerName;
  label: string;
  colour: string;
  hint: string;
}[] = [
  { id: "off_plan", label: "Off-plan", colour: "#7c3aed", hint: "Off-plan, resale channel" },
  { id: "developer", label: "Developer", colour: "#2563eb", hint: "Bought from the developer" },
  { id: "ready", label: "Ready", colour: "#0f766e", hint: "Completed, resale channel" },
  { id: "secondary", label: "Secondary", colour: "#b45309", hint: "No completion status recorded" },
  { id: "rental", label: "Rental", colour: "#be123c", hint: "On-market rent adverts" },
];

/** House marker. A divIcon: no asset to host, inherits the layer colour and
 *  stays crisp at any zoom. Anchored at the base so the point sits ON the
 *  location rather than above it. */
function houseIcon(colour: string): DivIcon {
  return new DivIcon({
    className: "",
    iconSize: [18, 18],
    iconAnchor: [9, 16],
    html: `<svg width="18" height="18" viewBox="0 0 24 24"
             style="filter:drop-shadow(0 1px 1px rgba(0,0,0,.35))">
             <path d="M12 3 2.5 10.5V21h7v-6h5v6h7V10.5L12 3Z"
                   fill="${colour}" stroke="#fff" stroke-width="1.5"
                   stroke-linejoin="round"/>
           </svg>`,
  });
}

const aedShort = (v: number | null): string => {
  if (v == null) return "—";
  if (v >= 1_000_000) return `AED ${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `AED ${Math.round(v / 1_000)}k`;
  return `AED ${Math.round(v)}`;
};

export function GeoActivityPanel() {
  const { data, isLoading } = useDashboardGeoActivity(14, 800);
  const layers = useDashboardGeoLayers(900);
  // Ready + rental on by default: the two largest on-market populations, and
  // the pair an operator most often compares. All five at once is 4,500
  // markers and reads as noise.
  const [active, setActive] = useState<Set<GeoLayerName>>(
    () => new Set<GeoLayerName>(["ready", "rental"]),
  );
  const toggle = (id: GeoLayerName) =>
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const points = useMemo(
    () =>
      (data ?? [])
        .filter((d) => d.lat != null && d.lon != null)
        .map((d) => ({
          lat: d.lat as number,
          lon: d.lon as number,
          community: d.community,
          ppsf: d.price_per_sqft_aed ?? null,
          price: d.listing_price_aed ?? null,
        })),
    [data],
  );

  return (
    <div className="rounded-lg border bg-white p-4">
      <h3 className="mb-2 text-sm font-semibold text-gray-700">
        Geo Activity · on-market inventory
      </h3>

      {/* Layer toggles. Each carries its own count, and a capped layer says
          so — a map showing 900 of 56,724 without saying which is a map that
          misrepresents the market (MPPI-00188). */}
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        {LAYERS.map((l) => {
          const seg = layers.data?.segments?.[l.id];
          const on = active.has(l.id);
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => toggle(l.id)}
              title={`${l.hint}${
                seg?.capped
                  ? ` — showing ${seg.shown.toLocaleString()} of ${seg.total.toLocaleString()}`
                  : ""
              }`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition ${
                on
                  ? "border-transparent text-white"
                  : "border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50"
              }`}
              style={on ? { backgroundColor: l.colour } : undefined}
            >
              <span
                className="inline-block h-2 w-2 rounded-sm"
                style={{ backgroundColor: on ? "#fff" : l.colour }}
              />
              {l.label}
              {seg && (
                <span className="tabular-nums opacity-80">
                  {seg.total.toLocaleString()}
                  {seg.capped ? "*" : ""}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* MPPI-00007 — relative so the loader can sit over the map. Without
          it an empty UAE outline is indistinguishable from a loading one. */}
      {/* h-72 to match the investor dashboard's map exactly (MPPI-00507). */}
      <div className="relative h-72 w-full overflow-hidden rounded-md">
        {isLoading && <BrandedLoader overlay label="Loading activity…" />}
        <MapContainer
          center={UAE_CENTER}
          zoom={7}
          minZoom={6}
          maxZoom={14}
          maxBounds={UAE_BOUNDS}
          maxBoundsViscosity={0.6}
          scrollWheelZoom={true}
          dragging={true}
          style={{ height: "100%", width: "100%" }}
        >
          {/* The same dark canvas the investor dashboard uses (MPPI-00507).
              This map was Esri's LIGHT grey; the operator asked for one look
              across both products, and a dense dot layer reads better when the
              base carries no colour of its own.

              The label layer is mounted after the data below, so place names
              sit ABOVE the dots rather than under them. */}
          <TileLayer
            url={TILES.minimal.url}
            attribution={TILES.minimal.attribution}
            maxZoom={16}
          />

          {/* City anchors — small dark dots with labels for orientation */}
          {UAE_CITY_ANCHORS.map((c) => (
            <Marker
              key={c.name}
              position={[c.lat, c.lon]}
              icon={L.divIcon({
                className: "city-anchor-icon",
                html:
                  `<span style="display:inline-flex;align-items:center;` +
                  `gap:4px;padding:1px 4px;background:rgba(255,255,255,.85);` +
                  `border-radius:3px;font-size:10px;font-weight:600;` +
                  `color:#1e293b;white-space:nowrap;">` +
                  `<span style="display:inline-block;width:5px;height:5px;` +
                  `background:#1e293b;border-radius:50%;"></span>` +
                  `${c.name}</span>`,
                iconSize: [80, 14],
                iconAnchor: [40, 7],
              })}
            />
          ))}

          {/* Inventory layers — one house per property, coloured by segment.
              The 14-day recency scatter is kept underneath as small dots:
              it answers "what is NEW", which the layers do not. */}
          {points.map((p, i) => (
            <CircleMarker
              key={`recent-${i}`}
              center={[p.lat, p.lon]}
              radius={3}
              pathOptions={{
                color: "#94a3b8",
                fillColor: "#94a3b8",
                fillOpacity: 0.5,
                weight: 0.5,
              }}
            >
              <Tooltip direction="top" offset={[0, -4]} opacity={0.95}>
                <div style={{ fontSize: 11 }}>
                  <strong>New listing · {p.community ?? "—"}</strong>
                  <br />
                  {p.price != null ? `AED ${Math.round(p.price).toLocaleString()}` : "—"}
                </div>
              </Tooltip>
            </CircleMarker>
          ))}

          {LAYERS.filter((l) => active.has(l.id)).map((layer) => {
            const seg = layers.data?.segments?.[layer.id];
            const icon = houseIcon(layer.colour);
            return (seg?.points ?? []).map((pt, i) => (
              <Marker
                key={`${layer.id}-${i}`}
                position={[pt.lat, pt.lon]}
                icon={icon}
              >
                <Tooltip direction="top" offset={[0, -12]} opacity={0.97}>
                  <div style={{ fontSize: 11, lineHeight: 1.45 }}>
                    <strong>{layer.label}</strong>
                    {" · "}
                    {pt.community ?? "Community unknown"}
                    <br />
                    {[
                      pt.bedrooms != null
                        ? pt.bedrooms === 0
                          ? "Studio"
                          : `${pt.bedrooms} bed`
                        : null,
                      pt.property_type ?? null,
                      pt.area_sqft != null
                        ? `${Math.round(pt.area_sqft).toLocaleString()} sqft`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "No description recorded"}
                    <br />
                    {/* Rent and price are the same key — say WHICH, always. */}
                    <strong>
                      {layer.id === "rental"
                        ? `${aedShort(pt.price)} / yr`
                        : aedShort(pt.price)}
                    </strong>
                    {pt.ppsf != null && (
                      <> · {Math.round(pt.ppsf).toLocaleString()} /sqft</>
                    )}
                  </div>
                </Tooltip>
              </Marker>
            ));
          })}
          <TileLayer url={TILES.minimal.ref as string} maxZoom={16} />
        </MapContainer>
      </div>
      <div className="mt-2 flex items-center justify-between">
        {/* The old legend described a price-per-sqft ramp that no longer
            exists — colour now carries the SEGMENT, and price moved to the
            tooltip. A legend for a retired encoding is worse than none. */}
        <div className="flex items-center gap-3 text-[10px] text-gray-500">
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-slate-400" />
            New in 14d
          </span>
          <span>Houses = on-market inventory · colour by segment</span>
          <span className="text-gray-400">* capped</span>
        </div>
        <p className="text-[9px] text-gray-400">
          Drag to pan · scroll / pinch to zoom
        </p>
      </div>
    </div>
  );
}
